import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import TransitionPortal from '@/components/Common/TransitionPortal';
import List from '@/components/List/List';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import FeedbackStaticModal from '@/features/Feedback/components/FeedbackStaticModal';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { stringSort } from '@/utils/util';

import S from '../../styles';
import type { EditTopicModalProps } from '../../types';
import UpperLevelDisplay from '../../UpperLevelDisplay/UpperLevelDisplay';
import createTopicUtils from '../../utils';
import AITopicCandidateItem from '../AITopicCandidateItem';
import FeedbackBlockPreviewItem from '../FeedbackBlockPreviewItem';
import type { CandidateItemT } from '../types';

export type EditTopicState = {
  data:
    | (FeedbackBlockT & {
        feedbackAlias: string;
      })[]
    | null;
  loading: boolean;
  fetched: boolean;
  error: boolean;
  init: boolean;
  static?: boolean;
};

const EditAiTopic = (
  props: EditTopicModalProps & {
    initialTopic: Topic2T<TOPIC_2_TYPE.AI>;
    target: CreateTopicTarget;
    targetTopicAlias?: string;
    closeModal: () => void;
  },
) => {
  const { modalState, status, close, open, setModalState } = useModalState<{
    feedbackAlias: string;
    blockAlias: string;
  } | null>(null);
  const [originTopic, setOriginTopic] = useState<CandidateItemT>({
    ...props.initialTopic,
    readyState: true,
    promptChanged: false,
    isLoading: false,
    isError: false,
  });
  const [topic, setTopic] = useState<CandidateItemT>({
    ...props.initialTopic,
    readyState: true,
    promptChanged: false,
    isLoading: false,
    isError: false,
  });

  const [previewStatus, setPreviewStatus] = useState(false);

  const hasChange = useMemo(() => {
    if (originTopic.name !== topic.name) return true;
    const originFilter = new SFilter(originTopic.ai_rule.filter || {});
    const topicFilter = new SFilter(topic.ai_rule.filter || {});
    if (
      createTopicUtils.compareRuleHasChange(
        originFilter.getIncludeSnippetFilter() || undefined,
        topicFilter.getIncludeSnippetFilter() || undefined,
      )
    )
      return true;
    if (
      createTopicUtils.compareRuleHasChange(
        originFilter.getExcludeSnippetFilter() || undefined,
        topicFilter.getExcludeSnippetFilter() || undefined,
      )
    )
      return true;
    return false;
  }, [
    originTopic.ai_rule.filter,
    originTopic.name,
    topic.ai_rule.filter,
    topic.name,
  ]);

  const fetchPreview = useCallback(async () => {
    try {
      setTopic((prev) => ({
        ...prev,
        isLoading: true,
      }));

      const baseParams: Topic2CreateParams<TOPIC_2_TYPE.AI> = {
        name: topic.name,
        category: TOPIC_2_TYPE.AI,
        ai_rule: {
          ...topic.ai_rule,
        },
      };

      let params:
        | GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'taxonomy'>
        | GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'topic'>;

      if (props.target === 'taxonomy') {
        params = {
          ...baseParams,
          taxonomy_id: props.taxonomyAlias,
        } as GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'taxonomy'>;
      } else {
        params = {
          ...baseParams,
          taxonomy_id: props.taxonomyAlias,
          parent_topic_id: props.targetTopicAlias,
        } as GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'topic'>;
      }

      const res = await service.taxonomy.fetchTopicPreviewFeedback(params);
      const blockList =
        res.feedbacks
          ?.map(
            (f) =>
              f.blocks?.map((block) => ({
                ...block,
                feedbackAlias: f.alias,
              })) || [],
          )
          .flat() || [];
      setTopic((prev) => ({
        ...prev,
        isLoading: false,
        irrelevant_feedback: [],
        relevant_feedback: blockList,
        readyState: true,
      }));
      setOriginTopic(topic);
      setPreviewStatus(true);
    } catch (e) {
      setTopic((prev) => ({
        ...prev,
        isError: true,
        isLoading: false,
      }));
    }
  }, [props.target, props.targetTopicAlias, props.taxonomyAlias, topic]);

  const submit = async () => {
    await service.taxonomy.updateTopicDetail(props.initialTopic.alias, {
      ai_rule: {
        ...topic.ai_rule,
      },
      relevant_feedback: topic.relevant_feedback?.map((f) => {
        return {
          block_id: f.alias,
          score: f.score || 0,
        };
      }),
      irrelevant_feedback: topic.irrelevant_feedback?.map((f) => {
        return {
          block_id: f.alias,
          score: f.score || 0,
        };
      }),
    });
    props.closeModal();
  };

  const sortedPreviewList = useMemo(() => {
    return (
      topic?.irrelevant_feedback
        ?.concat(topic.relevant_feedback || [])
        .sort((a, b) => stringSort(a.alias, b.alias)) || []
    );
  }, [topic?.irrelevant_feedback, topic.relevant_feedback]);

  return (
    <Style g={16}>
      <S.MainLayout style={{ width: 800 }}>
        <VFlex className="header">
          <Heading type="h2">Edit settings</Heading>
          <Typo type="body2" weight="m">
            Review and fine tune your prompts. Leverage keywords/filters for
            optimized results.
            <br />
            Decide your prompts carefully as the prompt cannot be updated once
            creation.
          </Typo>
        </VFlex>
        <Divider />
        <VFlex className="body" g={16} style={{ overflow: 'auto' }}>
          <UpperLevelDisplay
            target={props.target}
            targetTopicAlias={props.targetTopicAlias}
            taxonomyAlias={props.taxonomyAlias}
          />
          <AITopicCandidateItem
            index={0}
            candidateItem={topic}
            fetchPreview={fetchPreview}
            selected
            onPromptChange={() => {}}
            onRuleChange={(_, rule) => {
              setTopic((prev) => {
                return {
                  ...prev,
                  ai_rule: rule,
                  readyState: false,
                };
              });
            }}
            onTitleChange={(_, name) => name}
            editDisabled
          />
        </VFlex>
        <Divider />
        <Flex className="footer" j="flex-end">
          <Button variant="ghost" sizeType="xl" onClick={props.closeModal}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            sizeType="xl"
            onClick={submit}
            disabled={!topic.readyState || topic.isLoading || hasChange}
          >
            Done
          </Button>
        </Flex>
      </S.MainLayout>
      {previewStatus && (
        <S.PreviewLayout style={{ width: 500 }}>
          <VFlex f={1} g={24}>
            <Flex j="space-between" a="center">
              <Typo type="label1" weight="m">
                Preview
              </Typo>
              <Typo type="label3" className="text-gray-700">
                Select feedback blocks to train AI
              </Typo>
            </Flex>
            <List
              className="gap-2 flex-1"
              data={sortedPreviewList}
              isLoading={topic.isLoading}
              isError={topic.isError}
              placeholder={{
                node: topic.readyState ? (
                  <CenterFlex style={{ flex: 1 }}>
                    <Typo
                      type="body2"
                      className="text-gray-600"
                      style={{ textAlign: 'center' }}
                    >
                      There may be no associated feedback,
                      <br />
                      or the wrong prompt or keyword is set.
                    </Typo>
                  </CenterFlex>
                ) : (
                  <CenterFlex style={{ flex: 1 }}>
                    <Typo
                      type="body2"
                      className="text-gray-600"
                      style={{ textAlign: 'center' }}
                    >
                      You must update preview after
                      <br />
                      changing prompt or keyword filter
                    </Typo>
                  </CenterFlex>
                ),
              }}
              render={(block) => (
                <FeedbackBlockPreviewItem
                  key={block.alias}
                  block={block}
                  ignored={
                    !!topic.irrelevant_feedback?.find(
                      (item) => item.alias === block.alias,
                    )
                  }
                  onClick={(block) => {
                    setModalState({
                      feedbackAlias: block.feedbackAlias,
                      blockAlias: block.alias,
                    });
                    open();
                  }}
                  onIrrelevantClick={(block) => {
                    const newIrrelevantList = [
                      ...(topic.irrelevant_feedback || []),
                    ];
                    const newRelevantList = [
                      ...(topic.relevant_feedback || []),
                    ];
                    newIrrelevantList.upsert(
                      block,
                      (el) => el.alias === block.alias,
                    );
                    newRelevantList.remove(
                      block,
                      (el) => el.alias === block.alias,
                    );
                    setTopic((prev) => ({
                      ...prev,
                      irrelevant_feedback: newIrrelevantList,
                      relevant_feedback: newRelevantList,
                    }));
                  }}
                  onRestoreClick={(block) => {
                    const newIrrelevantList = [
                      ...(topic.irrelevant_feedback || []),
                    ];
                    const newRelevantList = [
                      ...(topic.relevant_feedback || []),
                    ];
                    newIrrelevantList.remove(
                      block,
                      (el) => el.alias === block.alias,
                    );
                    newRelevantList.upsert(
                      block,
                      (el) => el.alias === block.alias,
                    );
                    setTopic((prev) => ({
                      ...prev,
                      irrelevant_feedback: newIrrelevantList,
                      relevant_feedback: newRelevantList,
                    }));
                  }}
                />
              )}
            />
          </VFlex>
          <TransitionPortal
            layerId="syncly-popup-layer"
            status={status}
            background={{
              enable: true,
            }}
            close={close}
            style={{
              top: 48,
              right: 48,
            }}
          >
            {modalState && (
              <FeedbackStaticModal
                style={{
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  width: 600,
                }}
              >
                <FeedbackStaticModal.Header
                  closeModal={close}
                  feedbackAlias={modalState.feedbackAlias}
                />

                <FeedbackStaticModal.Body
                  feedbackAlias={modalState.feedbackAlias}
                  focusedBlock={modalState.blockAlias}
                  style={{
                    maxHeight: '80vh',
                    minHeight: '50vh',
                  }}
                />
              </FeedbackStaticModal>
            )}
          </TransitionPortal>
        </S.PreviewLayout>
      )}
    </Style>
  );
};

const Style = styled(Flex)`
  max-height: calc(100vh - 48px);
`;

export default EditAiTopic;
